import { isStackableTerminalToolCallMessage } from "../Messages/CompletedToolCallStack";
import { ChatMessage } from "../stores";
import { MessageRole, ToolCallStatus } from "../types";

import { isComputerAgentToolName } from "./toolNameMatchers";

export enum VisibleMessageRowType {
  ToolActivity = "tool_activity",
  ToolStack = "tool_stack",
  Message = "message",
}

const isVisibleToolCall = (message: ChatMessage) =>
  message.role === MessageRole.TOOL_CALL &&
  message.toolCall &&
  !isComputerAgentToolName(message.toolCall.name);

const isActiveToolCall = (message: ChatMessage) =>
  isVisibleToolCall(message) &&
  (message.toolCall?.status === ToolCallStatus.RUNNING ||
    message.toolCall?.status === ToolCallStatus.PENDING ||
    message.toolCall?.status === ToolCallStatus.COMPLETED);

const hasAssistantTextAfter = (messages: ChatMessage[], startIndex: number) =>
  messages
    .slice(startIndex + 1)
    .some(
      message =>
        (message.role === MessageRole.ASSISTANT ||
          message.role === MessageRole.MASTER_VIBE_PROMPT) &&
        message.content.trim().length > 0,
    );

const collectFollowingToolCalls = (messages: ChatMessage[], startIndex: number) => {
  const toolCalls: ChatMessage[] = [];

  for (let index = startIndex + 1; index < messages.length; index++) {
    const message = messages[index];
    if (isActiveToolCall(message)) {
      toolCalls.push(message);
      continue;
    }
    if (message.role === MessageRole.ASSISTANT && !message.content.trim()) {
      continue;
    }
    break;
  }

  return toolCalls;
};

export type VisibleMessageRow =
  | {
      type: VisibleMessageRowType.ToolActivity;
      key: string;
      toolCalls: ChatMessage[];
    }
  | {
      type: VisibleMessageRowType.ToolStack;
      key: string;
      toolCalls: ChatMessage[];
    }
  | {
      type: VisibleMessageRowType.Message;
      key: string;
      message: ChatMessage;
      index: number;
      isLastMessage: boolean;
    };

export const buildVisibleMessageRows = (
  messages: ChatMessage[],
  isLoading: boolean,
): VisibleMessageRow[] => {
  const rows: VisibleMessageRow[] = [];
  const skippedIndices = new Set<number>();

  for (let index = 0; index < messages.length; index++) {
    if (skippedIndices.has(index)) {
      continue;
    }

    const message = messages[index];
    const isLastMessage = index === messages.length - 1;

    const shouldAggregateToolActivity =
      isLoading &&
      message.role === MessageRole.ASSISTANT &&
      !message.content.trim() &&
      !hasAssistantTextAfter(messages, index);
    const aggregatedToolCalls = shouldAggregateToolActivity
      ? collectFollowingToolCalls(messages, index)
      : [];

    if (aggregatedToolCalls.length > 0) {
      aggregatedToolCalls.forEach((_, offset) => {
        skippedIndices.add(index + offset + 1);
      });
      rows.push({
        type: VisibleMessageRowType.ToolActivity,
        key: `tool-activity-${message.taskId ?? index}`,
        toolCalls: aggregatedToolCalls,
      });
      continue;
    }

    if (
      isLoading &&
      isVisibleToolCall(message) &&
      !hasAssistantTextAfter(messages, index) &&
      messages
        .slice(0, index)
        .some(
          previousMessage =>
            previousMessage.role === MessageRole.ASSISTANT && !previousMessage.content.trim(),
        )
    ) {
      continue;
    }

    if (isStackableTerminalToolCallMessage(message)) {
      const previousMessage = messages[index - 1];
      if (previousMessage && isStackableTerminalToolCallMessage(previousMessage)) {
        continue;
      }

      const stack: ChatMessage[] = [];
      for (let stackIndex = index; stackIndex < messages.length; stackIndex++) {
        const stackMessage = messages[stackIndex];
        if (!isStackableTerminalToolCallMessage(stackMessage)) {
          break;
        }
        stack.push(stackMessage);
        skippedIndices.add(stackIndex);
      }

      rows.push({
        type: VisibleMessageRowType.ToolStack,
        key: `tool-stack-${message.toolCall?.id ?? index}`,
        toolCalls: stack,
      });
      continue;
    }

    let messageKey = `${message.role}-${index}`;
    if (message.taskId) {
      messageKey = message.taskId;
    } else if (message.toolCall?.id) {
      messageKey = `tool-${message.toolCall.id}`;
    }

    rows.push({
      type: VisibleMessageRowType.Message,
      key: messageKey,
      message,
      index,
      isLastMessage,
    });
  }

  return rows;
};
